import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, AlertTriangle } from 'lucide-react';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';
import { deleteUserAccount } from '../../../store/actions/authActions';
import { useDispatch } from 'react-redux';
import styles from './DeleteAccountModal.module.scss';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  isUserPremium: boolean;
  isFreeTrialActive: boolean;
}

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
};

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  isUserPremium,
  isFreeTrialActive,
}) => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const handleDelete = () => {
    if (isUserPremium || isFreeTrialActive) {
      dispatch(deleteUserAccount() as any);
    } else {
      dispatch(deleteUserAccount(message || null) as any);
    }
    onClose();
  };

  const handleCancel = () => {
    setMessage('');
    onClose();
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleCancel} zIndex={1000}>
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalHeader_icon}>
            <AlertTriangle size={24} />
          </div>
          <h2 className={styles.modalHeader_title}>
            {isUserPremium || isFreeTrialActive
              ? 'Eliminar cuenta Premium'
              : 'Eliminar cuenta'}
          </h2>
          <button
            className={styles.modalHeader_close}
            onClick={handleCancel}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        <div className={styles.modalContent}>
          {isUserPremium || isFreeTrialActive ? (
            <div className={styles.warningSection}>
              <p className={styles.warningText}>
                Al eliminar tu cuenta, perderás todos los beneficios adquiridos:
              </p>
              <ul className={styles.warningList}>
                <li>Acceso a tu plan Premium</li>
                {isFreeTrialActive && <li>Tu período de prueba gratuito</li>}
                <li>Todos tus proyectos y publicaciones</li>
                <li>Tu historial de actividad</li>
              </ul>
              <p className={styles.confirmationText}>
                ¿Estás seguro de que deseas eliminar tu cuenta? Esta acción no se puede deshacer.
              </p>
            </div>
          ) : (
            <div className={styles.infoSection}>
              <p className={styles.infoText}>
                Tu cuenta básica es completamente gratuita y siempre lo será. No tiene ningún costo asociado.
              </p>
              <p className={styles.messagePrompt}>
                Si aún así deseas eliminar tu cuenta, por favor déjanos saber el motivo. Esto nos ayuda a mejorar el servicio.
              </p>
              <div className={styles.messageInput}>
                <label htmlFor="deleteMessage" className={styles.messageLabel}>
                  Motivo (opcional)
                </label>
                <textarea
                  id="deleteMessage"
                  className={styles.textarea}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ej: Ya no uso la plataforma, encontré otra alternativa, etc."
                  rows={4}
                />
              </div>
            </div>
          )}

          <div className={styles.modalActions}>
            <button
              className={styles.cancelButton}
              onClick={handleCancel}
            >
              Cancelar
            </button>
            <button
              className={styles.deleteButton}
              onClick={handleDelete}
            >
              Eliminar cuenta
            </button>
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

export default DeleteAccountModal;

